import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { HeaderProfile } from 'widgets/header-profile'
import { tabsChangeSession } from 'widgets/header-tabs/module'

import userStore from 'entities/user/module/user-store'

import { GtagEventKey } from 'shared/constants/analytics'
import { sendGtagEvent } from 'shared/lib/analytics'
import { getHeaderBackgroundColor } from 'shared/lib/getHeaderBackgroundColor.ts'
import { getUserIsDevelopers } from 'shared/lib/userIsDeveloper'
import { PagesModule, PagesModuleRouter, TabsPages } from 'shared/ui/tabs-pages'

import s from './styles.module.scss'

interface Props {
  module: PagesModule
  isReplace?: boolean
  isTicket?: boolean
}

const serviceClickEvents: Partial<Record<PagesModule, GtagEventKey>> = {
  avia: 'SERVICE_AVIA_CLICK',
  tours: 'SERVICE_TOURS_CLICK',
  kyrgyzstan: 'SERVICE_HOTELS_CLICK',
  excursion: 'SERVICE_EXCURSIONS_CLICK',
}

export const HeaderTabs = ({ module, isReplace, isTicket }: Props) => {
  const navigate = useNavigate()

  const containerRef = useRef<HTMLDivElement>(null)
  const tabsContainerRef = useRef<HTMLDivElement>(null)

  const [isHeaderTabsVisible, setIsHeaderTabsVisible] = useState(true)

  const isDevUser = getUserIsDevelopers()
  const { setPageModule } = tabsChangeSession()

  const backgroundColor = PagesModuleRouter?.[module]?.newTabs?.backgroundColor
  const headerBackgroundColor = getHeaderBackgroundColor(module)

  const onChangeModule = (module: PagesModule) => {
    if (!PagesModuleRouter[module]?.[module]) return

    const event = serviceClickEvents[module]
    if (event) sendGtagEvent(event)

    navigate(PagesModuleRouter[module]?.[module] as string, {
      replace: isReplace,
    })

    setPageModule(module)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsHeaderTabsVisible(entry.isIntersecting)
        })
      },
      {
        rootMargin: '-20px',
        threshold: 0,
      },
    )

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [])

  useLayoutEffect(() => {
    const tabsContainer = tabsContainerRef.current
    if (!tabsContainer) return

    const activeTab = tabsContainer.querySelector(`[data-module="${module}"]`)

    if (!activeTab) return

    activeTab.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    })
  }, [module])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const userType =
    userStore?.user_authority_type || userStore?.account?.mbank_user_role
  return (
    <div
      ref={containerRef}
      style={{ background: backgroundColor }}
      className={s.container}
    >
      {/* <SnowfallComponent /> */}
      <div
        className={clsx(
          s.headerProfile,
          !isHeaderTabsVisible && s.headerProfileFixed,
        )}
      >
        <HeaderProfile
          headerVariant="transparent"
          backgroundColor={
            !isHeaderTabsVisible ? headerBackgroundColor : undefined
          }
          isFixed={false}
        />
      </div>

      {!isHeaderTabsVisible && (
        <div className={clsx('safe-area-top-padding', s.headerPlaceholder)} />
      )}

      <div
        ref={tabsContainerRef}
        className={s.tabs}
      >
        <TabsPages
          isNewVersion
          active={module}
          isTicket={isTicket}
          isDevUser={isDevUser}
          authorityType={userType}
          onClick={onChangeModule}
        />
      </div>
    </div>
  )
}
